import {
  buildDealPayrollGroupSettingsRequest, DEAL_PAYROLL_COEFFICIENT_MODE,
  DealPayrollArea, DealPayrollCoefficientMode, DealPayrollCoefficientUnit,
  DealPayrollGrade, DealPayrollGradeCode, DealPayrollGroupSettings,
  DealPayrollPosition, DealPayrollRates, DealPayrollRatesRequest,
  isFinitePayrollNumber, isValidPlannedPaymentGraceDays, PAYROLL_CALCULATION_METHOD,
  UserPayrollSettingsRequest, UserPayrollSettingsResponse
} from './deal-payroll.model';
import {
  AREA_CATALOG_UNAVAILABLE_EMPTY_KEY,
  AREAS_INVALID_KEY,
  BUSINESS_TRIP_COEFFICIENT_INVALID_KEY,
  COEFFICIENT_MODE_KEY_PREFIX,
  DEAL_PAYROLL_SETTINGS_EMPTY_LENGTH,
  DEAL_PAYROLL_SETTINGS_EMPTY_TEXT,
  DEAL_PAYROLL_SETTINGS_FIRST_INDEX,
  DEAL_PAYROLL_SETTINGS_FORBIDDEN_KEY,
  DEAL_PAYROLL_SETTINGS_LOAD_ERROR_KEY,
  DEAL_PAYROLL_SETTINGS_STRING_TYPE,
  DECIMAL_ROUNDING_TOLERANCE,
  EMPTY_SETTING_VALUE,
  FOUR_DECIMAL_SCALE_FACTOR,
  GRADE_REQUIRED_KEY,
  MASTER_COEFFICIENT_INVALID_KEY,
  MAXIMUM_COEFFICIENT_VALUE,
  MAXIMUM_HOURLY_RATE_VALUE,
  MINIMUM_NON_NEGATIVE_VALUE,
  POSITION_KEY_SEPARATOR,
  POSITION_REQUIRED_KEY,
  REQUIRED_GRADE_SEQUENCE,
  REQUIRED_TEAM_UNIT_COUNT,
  SINGLE_UNIT_COUNT,
  TEAM_COEFFICIENTS_INVALID_KEY,
  TEAM_H_CODE,
  TEAM_R_CODE,
  TEAM_UNIT_TYPE
} from './deal-payroll-settings.constants';
import {DealPayrollPositionOption} from './deal-payroll-settings.types';
import {CurrencyCodes, PAYROLL_CURRENCY_CODES} from './payroll-currency.model';

const MONTHLY_BASE_INVALID_KEY = 'DEAL_PAYROLL_MONTHLY_BASE_INVALID';
const WEIGHT_INVALID_KEY = 'DEAL_PAYROLL_WEIGHT_INVALID';
const TASK_GRADES_INVALID_KEY = 'DEAL_PAYROLL_TASK_GRADES_INVALID';
const FULL_WEIGHT = 1;
const PLANNED_GRACE_INVALID_KEY = 'DEAL_PAYROLL_PLANNED_GRACE_INVALID';
const ENABLED_GRADE_REQUIRED_KEY = 'DEAL_PAYROLL_ENABLED_GRADE_REQUIRED';

export {DealPayrollPositionOption} from './deal-payroll-settings.types';

export function dealPayrollPositionOptions(
  settings: UserPayrollSettingsResponse
): DealPayrollPositionOption[] {
  return (settings && settings.payrollPositions || [])
    .filter((position: DealPayrollPosition) => validPositionOption(position))
    .map((position: DealPayrollPosition) => ({
      key: dealPayrollPositionKey(position), ownerGroupId: position.ownerGroupId,
      ownerGroupName: position.ownerGroupName || DEAL_PAYROLL_SETTINGS_EMPTY_TEXT,
      positionId: position.positionId,
      displayName: position.displayName
    }));
}

export function dealPayrollPositionKey(position: DealPayrollPosition): string {
  return position.ownerGroupId + POSITION_KEY_SEPARATOR + position.positionId;
}

export function buildDealPayrollUserSettingsRequest(
  settings: UserPayrollSettingsResponse, positionKey: string, gradeCode: DealPayrollGradeCode
): UserPayrollSettingsRequest {
  if (!settings) { return null; }
  const grade = selectedGrade(settings, gradeCode);
  const assignmentRequired = hasAssignmentChoices(settings);
  if (!grade) {
    return null;
  }
  return {
    payrollCalculationMethod: PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL,
    gradeCode: grade ? grade.gradeCode : null,
    assignmentRevision: assignmentRequired ? settings.assignmentRevision : null,
    expectedUserRevision: settings.userRevision
  };
}

export function cloneDealPayrollGroupSettings(
  settings: DealPayrollGroupSettings
): DealPayrollGroupSettings {
  return Object.assign({}, settings, {
    payrollPositions: cloneRows(settings.payrollPositions), grades: cloneRows(settings.grades),
    areas: cloneRows(settings.areas), coefficientUnits: cloneRows(settings.coefficientUnits),
    blockers: (settings.blockers || []).slice()
  }) as DealPayrollGroupSettings;
}

export function restoreDealPayrollGroupSettingsDraft(
  fresh: DealPayrollGroupSettings,
  draft: DealPayrollGroupSettings
): DealPayrollGroupSettings {
  if (!fresh || !draft || fresh.groupId !== draft.groupId) {
    return fresh;
  }
  const restored = cloneDealPayrollGroupSettings(draft);
  restored.revision = fresh.revision;
  restored.policyGroupId = fresh.policyGroupId;
  restored.policyGroupName = fresh.policyGroupName;
  restored.effectiveFromMonth = fresh.effectiveFromMonth;
  restored.updatedAt = fresh.updatedAt;
  restored.updatedBy = fresh.updatedBy;
  restored.canManage = fresh.canManage;
  restored.eligible = fresh.eligible;
  restored.blockers = (fresh.blockers || []).slice();
  refreshSavedPreviewRates(restored, fresh);
  return restored;
}

function refreshSavedPreviewRates(
  restored: DealPayrollGroupSettings, fresh: DealPayrollGroupSettings
): void {
  restored.effectiveGrowthProfessionalProfile = fresh.effectiveGrowthProfessionalProfile;
  restored.rateMonth = fresh.rateMonth;
  restored.workingHoursPerMonth = fresh.workingHoursPerMonth;
  restored.baseHourRate = fresh.baseHourRate;
  restored.plannedHourRate = fresh.plannedHourRate;
  restored.actualHourRate = fresh.actualHourRate;
  refreshSavedGradeMetadata(restored.grades, fresh.grades);
}

function refreshSavedGradeMetadata(grades: DealPayrollGrade[], fresh: DealPayrollGrade[]): void {
  grades.forEach(grade => {
    const saved = (fresh || []).find(candidate => candidate.gradeCode === grade.gradeCode);
    grade.baseHourRate = saved && saved.baseHourRate;
    grade.plannedHourRate = saved && saved.plannedHourRate;
    grade.actualHourRate = saved && saved.actualHourRate;
    grade.inUse = !!saved && saved.inUse === true;
    if (grade.inUse) { grade.enabled = true; }
  });
}

export function isDealPayrollGroupSettingsSaveable(settings: DealPayrollGroupSettings): boolean {
  return dealPayrollGroupSettingsProblemKey(settings) === DEAL_PAYROLL_SETTINGS_EMPTY_TEXT;
}

export function dealPayrollGroupSettingsProblemKey(settings: DealPayrollGroupSettings): string {
  if (!settings) {
    return DEAL_PAYROLL_SETTINGS_LOAD_ERROR_KEY;
  }
  if (settings.canManage !== true) {
    return DEAL_PAYROLL_SETTINGS_FORBIDDEN_KEY;
  }
  const rowProblemKey = groupSettingsRowProblemKey(settings);
  if (rowProblemKey) { return rowProblemKey; }
  if (settings.coefficientMode === DEAL_PAYROLL_COEFFICIENT_MODE.AREA) {
    return areaSettingsProblemKey(settings);
  }
  if (settings.coefficientMode === DEAL_PAYROLL_COEFFICIENT_MODE.TEAM) {
    return teamSettingsProblemKey(settings);
  }
  return settings.coefficientMode === DEAL_PAYROLL_COEFFICIENT_MODE.TASK_GRADE
    ? taskGradeSettingsProblemKey(settings) : settings.coefficientMode === DEAL_PAYROLL_COEFFICIENT_MODE.NONE
    ? DEAL_PAYROLL_SETTINGS_EMPTY_TEXT : TEAM_COEFFICIENTS_INVALID_KEY;
}

function groupSettingsRowProblemKey(settings: DealPayrollGroupSettings): string {
  if (settings.businessTripCoefficient !== undefined && !isValidCoefficient(settings.businessTripCoefficient)) {
    return BUSINESS_TRIP_COEFFICIENT_INVALID_KEY;
  }
  if (settings.plannedPaymentGraceDays !== undefined
    && !isValidPlannedPaymentGraceDays(settings.plannedPaymentGraceDays)) {
    return PLANNED_GRACE_INVALID_KEY;
  }
  if (!isValidPositiveHourlyRate(settings.baseMonthlySalary)) {
    return MONTHLY_BASE_INVALID_KEY;
  }
  if (!validPlannedWeight(settings.plannedWeight) || (settings.grades || []).some(grade =>
    !!grade && grade.plannedWeight != null && !validPlannedWeight(grade.plannedWeight))) {
    return WEIGHT_INVALID_KEY;
  }
  if (!validGradeSequence(settings.grades)) { return GRADE_REQUIRED_KEY; }
  return settings.grades.some(grade => grade.enabled) ? DEAL_PAYROLL_SETTINGS_EMPTY_TEXT : ENABLED_GRADE_REQUIRED_KEY;
}

function validPlannedWeight(weight: number): boolean {
  return isFinitePayrollNumber(weight) && weight >= MINIMUM_NON_NEGATIVE_VALUE
    && weight <= FULL_WEIGHT && hasFourDecimalScale(weight);
}

export function dealPayrollTeamCoefficientUnits(
  settings: DealPayrollGroupSettings
): DealPayrollCoefficientUnit[] {
  const units = settings && settings.coefficientUnits || [];
  const teamUnits = units.filter((unit: DealPayrollCoefficientUnit) =>
    !!unit && unit.type === TEAM_UNIT_TYPE);
  const ordered: DealPayrollCoefficientUnit[] = [];
  appendTeamUnit(ordered, teamUnits, TEAM_R_CODE);
  appendTeamUnit(ordered, teamUnits, TEAM_H_CODE);
  return ordered;
}

export function isDealPayrollGroupSettingsDraftPristine(
  settings: DealPayrollGroupSettings,
  authoritative: DealPayrollGroupSettings
): boolean {
  return !!settings && !!authoritative
    && settings.groupId === authoritative.groupId
    && JSON.stringify(buildDealPayrollGroupSettingsRequest(settings))
      === JSON.stringify(buildDealPayrollGroupSettingsRequest(authoritative));
}

export function dealPayrollCatalogKey(settings: DealPayrollGroupSettings): string {
  return settings && settings.groupId || DEAL_PAYROLL_SETTINGS_EMPTY_TEXT;
}

export function dealPayrollSettingValue(value: number): string {
  return isFinitePayrollNumber(value) ? String(value) : EMPTY_SETTING_VALUE;
}

export function dealPayrollCoefficientModeKey(mode: DealPayrollCoefficientMode): string {
  return COEFFICIENT_MODE_KEY_PREFIX + (mode || DEAL_PAYROLL_COEFFICIENT_MODE.NONE);
}

export function buildDealPayrollRatesRequest(settings: DealPayrollRates): DealPayrollRatesRequest {
  return {
    actualHourRate: settings.actualHourRate, juniorCoefficient: settings.juniorCoefficient,
    middleCoefficient: settings.middleCoefficient,
    internalGroupIds: Object.assign({}, settings.internalGroupIds || {}),
    revision: settings.revision
  };
}

export function isDealPayrollRatesSaveable(settings: DealPayrollRates): boolean {
  return !!settings && isFinitePayrollNumber(settings.actualHourRate)
    && isFinitePayrollNumber(settings.juniorCoefficient)
    && isFinitePayrollNumber(settings.middleCoefficient)
    && isFinitePayrollNumber(settings.revision);
}

export function restoreDealPayrollRatesDraft(
  fresh: DealPayrollRates,
  draft: DealPayrollRates
): DealPayrollRates {
  if (!fresh || !draft) {
    return fresh;
  }
  return Object.assign({}, draft, {
    revision: fresh.revision, updatedAt: fresh.updatedAt, updatedBy: fresh.updatedBy,
    internalGroupIds: Object.assign({}, draft.internalGroupIds || {})
  }) as DealPayrollRates;
}

function validPositionOption(position: DealPayrollPosition): boolean {
  return !!position && position.active === true && hasText(position.ownerGroupId)
    && hasText(position.positionId) && hasText(position.displayName);
}

function selectedPosition(
  settings: UserPayrollSettingsResponse,
  positionKey: string
): DealPayrollPosition {
  return (settings.payrollPositions || []).filter((candidate: DealPayrollPosition) =>
    validPositionOption(candidate) && dealPayrollPositionKey(candidate) === positionKey)
    [DEAL_PAYROLL_SETTINGS_FIRST_INDEX];
}

function selectedGrade(
  settings: UserPayrollSettingsResponse,
  gradeCode: DealPayrollGradeCode
): DealPayrollGrade {
  return (settings.grades || []).filter((candidate: DealPayrollGrade) =>
    !!candidate && candidate.enabled === true && candidate.gradeCode === gradeCode)
    [DEAL_PAYROLL_SETTINGS_FIRST_INDEX];
}

function hasAssignmentChoices(settings: UserPayrollSettingsResponse): boolean {
  return !!settings
    && (settings.grades || []).length > DEAL_PAYROLL_SETTINGS_EMPTY_LENGTH;
}

function validPosition(position: DealPayrollPosition): boolean {
  return !!position && hasText(position.displayName)
    && (!position.rateCurrency || PAYROLL_CURRENCY_CODES.indexOf(position.rateCurrency)
      >= MINIMUM_NON_NEGATIVE_VALUE)
    && isValidHourlyRate(position.actualHourRate)
    && (isValidPositiveHourlyRate(position.plannedHourRate)
      || (!position.rateCurrency || position.rateCurrency === CurrencyCodes.BYN)
      && (position.plannedHourRate === null || position.plannedHourRate === undefined))
    && position.active !== null && position.active !== undefined;
}

function hasUniqueNormalizedPositionNames(positions: DealPayrollPosition[]): boolean {
  const names = positions.map((position: DealPayrollPosition) =>
    position.displayName.trim().toLowerCase());
  return names.every((name: string, index: number) => names.indexOf(name) === index);
}

function validGrade(grade: DealPayrollGrade): boolean {
  return !!grade && !!grade.gradeCode && (grade.enabled === true || grade.enabled === false)
    && isNonNegativeNumber(grade.minPoints)
    && isValidCoefficient(grade.coefficient)
    && (grade.gradeCode !== REQUIRED_GRADE_SEQUENCE[DEAL_PAYROLL_SETTINGS_FIRST_INDEX]
      || grade.coefficient === FULL_WEIGHT);
}

function validGradeSequence(grades: DealPayrollGrade[]): boolean {
  const ordered = orderedGrades(grades);
  return ordered.length === REQUIRED_GRADE_SEQUENCE.length && ordered.every(validGrade);
}

function orderedGrades(grades: DealPayrollGrade[]): DealPayrollGrade[] {
  if (!grades || grades.length !== REQUIRED_GRADE_SEQUENCE.length) {
    return [];
  }
  return REQUIRED_GRADE_SEQUENCE.map((code: DealPayrollGradeCode) =>
    grades.filter((grade: DealPayrollGrade) => !!grade && grade.gradeCode === code)
    [DEAL_PAYROLL_SETTINGS_FIRST_INDEX]);
}

function validArea(area: DealPayrollArea): boolean {
  return !!area && isPositiveNumber(area.directoryId) && isPositiveNumber(area.areaId)
    && hasText(area.displayName) && isValidCoefficient(area.coefficient);
}

function areaSettingsProblemKey(settings: DealPayrollGroupSettings): string {
  const areas = settings.areas || [];
  if (settings.areaCatalogAvailable !== true && !nonEmpty(areas)) {
    return AREA_CATALOG_UNAVAILABLE_EMPTY_KEY;
  }
  return areas.every(validArea) ? DEAL_PAYROLL_SETTINGS_EMPTY_TEXT : AREAS_INVALID_KEY;
}

function teamSettingsProblemKey(settings: DealPayrollGroupSettings): string {
  const teamUnits = dealPayrollTeamCoefficientUnits(settings);
  if (teamUnits.length !== REQUIRED_TEAM_UNIT_COUNT
      || !hasExactTeamCodes(settings.coefficientUnits || [])
      || !teamUnits.every(validTeamUnit)) {
    return TEAM_COEFFICIENTS_INVALID_KEY;
  }
  const master = settings.masterCoefficient;
  return master === null || master === undefined || isValidCoefficient(master)
    ? DEAL_PAYROLL_SETTINGS_EMPTY_TEXT : MASTER_COEFFICIENT_INVALID_KEY;
}

function taskGradeSettingsProblemKey(settings: DealPayrollGroupSettings): string {
  const units = (settings.coefficientUnits || []).filter(unit => !!unit && unit.type === DEAL_PAYROLL_COEFFICIENT_MODE.TASK_GRADE);
  const valid = units.length === REQUIRED_GRADE_SEQUENCE.length
    && REQUIRED_GRADE_SEQUENCE.every(code => units.filter(unit => unit.code === code).length === SINGLE_UNIT_COUNT)
    && units.every(unit => unit.editable === true && hasText(unit.displayName) && isValidCoefficient(unit.coefficient));
  return valid ? DEAL_PAYROLL_SETTINGS_EMPTY_TEXT : TASK_GRADES_INVALID_KEY;
}

function validTeamUnit(unit: DealPayrollCoefficientUnit): boolean {
  return !!unit && unit.type === TEAM_UNIT_TYPE && hasText(unit.code)
    && unit.editable === true && isValidCoefficient(unit.coefficient);
}

function hasExactTeamCodes(units: DealPayrollCoefficientUnit[]): boolean {
  const teamUnits = (units || []).filter((unit: DealPayrollCoefficientUnit) =>
    !!unit && unit.type === TEAM_UNIT_TYPE && unit.editable === true);
  return teamUnits.length === REQUIRED_TEAM_UNIT_COUNT
    && teamUnits.filter((unit: DealPayrollCoefficientUnit) =>
      unit.code === TEAM_R_CODE).length === SINGLE_UNIT_COUNT
    && teamUnits.filter((unit: DealPayrollCoefficientUnit) =>
      unit.code === TEAM_H_CODE).length === SINGLE_UNIT_COUNT;
}

function appendTeamUnit(
  target: DealPayrollCoefficientUnit[],
  units: DealPayrollCoefficientUnit[],
  code: string
): void {
  const unit = units.filter((candidate: DealPayrollCoefficientUnit) => candidate.code === code)
    [DEAL_PAYROLL_SETTINGS_FIRST_INDEX];
  if (unit) {
    target.push(unit);
  }
}

function isPositiveNumber(value: number): boolean {
  return isFinitePayrollNumber(value) && value > MINIMUM_NON_NEGATIVE_VALUE;
}

function isNonNegativeNumber(value: number): boolean {
  return isFinitePayrollNumber(value) && value >= MINIMUM_NON_NEGATIVE_VALUE;
}

function isValidCoefficient(value: number): boolean {
  return isPositiveNumber(value) && value <= MAXIMUM_COEFFICIENT_VALUE
    && hasFourDecimalScale(value);
}

function isValidHourlyRate(value: number): boolean {
  return isFinitePayrollNumber(value) && value >= MINIMUM_NON_NEGATIVE_VALUE
    && value <= MAXIMUM_HOURLY_RATE_VALUE && hasFourDecimalScale(value);
}

function isValidPositiveHourlyRate(value: number): boolean {
  return isFinitePayrollNumber(value) && value > MINIMUM_NON_NEGATIVE_VALUE
    && value <= MAXIMUM_HOURLY_RATE_VALUE && hasFourDecimalScale(value);
}

function hasFourDecimalScale(value: number): boolean {
  const scaled = value * FOUR_DECIMAL_SCALE_FACTOR;
  return Math.abs(scaled - Math.round(scaled)) < DECIMAL_ROUNDING_TOLERANCE;
}

function nonEmpty(values: Object[]): boolean {
  return !!values && values.length > DEAL_PAYROLL_SETTINGS_EMPTY_LENGTH;
}

function hasText(value: string): boolean {
  return typeof value === DEAL_PAYROLL_SETTINGS_STRING_TYPE
    && value.trim().length > DEAL_PAYROLL_SETTINGS_EMPTY_LENGTH;
}

function cloneRows<T>(values: T[]): T[] {
  return (values || []).map((value: T) => Object.assign({}, value));
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/models/deal-payroll-settings.adapter.ts